import React from 'react';
import { Tooltip } from 'antd';

/* 역할 배지 — 색은 '역할 이름'이 아니라 '실제 가진 권한 등급'으로 정한다.
   역할은 DB 에서 자유롭게 만들 수 있어(선임연구원·주임연구원 등) role_code 로 색을 박으면
   새로 만든 역할이 전부 같은 색으로 떨어진다. 등급은 백엔드 `_role_perm_summary()` 가 계산해
   `perm_tier` 로 내려주며, 사용자관리·역할관리 화면이 같은 값을 쓴다.

   색만으로 구분하지 않는다 — 등급 아이콘(형태)과 툴팁의 권한 요약이 의미를 함께 전달한다.
   팔레트는 전 조합 ΔE00 정상 23.1 이상 / 색약(protan·deutan·tritan) 10.1 이상,
   글자↔배경 명도대비 4.55 이상으로 검증했다. 값을 바꾸면 다시 검증할 것. */

export type RoleTier = 'admin' | 'manage' | 'edit' | 'read' | 'none';

export interface RolePerm {
  id: number;
  role_name: string;
  role_code?: string;
  is_system?: boolean;
  perm_tier?: RoleTier | string;
  perm_menus?: number;
  perm_read?: number;
  perm_write?: number;
  perm_delete?: number;
  perm_actions?: number;
}

export const ROLE_TIER_META: Record<RoleTier, {
  label: string; color: string; bg: string; border: string; icon: string; desc: string;
}> = {
  admin:  { label: '시스템관리', color: '#9f1239', bg: '#fff1f2', border: '#fecdd3', icon: '★',
            desc: '계정·권한을 변경할 수 있는 최상위 등급' },
  manage: { label: '관리',       color: '#b45309', bg: '#fffbeb', border: '#fde68a', icon: '◆',
            desc: '삭제까지 가능한 관리 등급' },
  edit:   { label: '편집',       color: '#6b21a8', bg: '#faf5ff', border: '#e9d5ff', icon: '▲',
            desc: '등록·수정 가능(삭제 없음)' },
  read:   { label: '열람',       color: '#115e59', bg: '#f0fdfa', border: '#99f6e4', icon: '●',
            desc: '조회만 가능' },
  none:   { label: '권한없음',   color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: '○',
            desc: '부여된 권한 없음' },
};

export const TIER_ORDER: RoleTier[] = ['admin', 'manage', 'edit', 'read', 'none'];

export const roleTier = (r?: Pick<RolePerm, 'perm_tier'> | null): RoleTier => {
  const t = r?.perm_tier as RoleTier | undefined;
  return t && ROLE_TIER_META[t] ? t : 'none';
};

/** 사용자가 가진 역할 중 가장 높은 등급 (정렬·요약용) */
export const highestTier = (roles?: RolePerm[] | null): RoleTier => {
  if (!roles || roles.length === 0) return 'none';
  for (const t of TIER_ORDER) {
    if (roles.some(r => roleTier(r) === t)) return t;
  }
  return 'none';
};

const pill = (m: typeof ROLE_TIER_META[RoleTier], size: 'sm' | 'md'): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: size === 'sm' ? '0 8px' : '2px 10px',
  borderRadius: 999,
  fontSize: size === 'sm' ? 11 : 12, fontWeight: 700,
  color: m.color, background: m.bg, border: `1px solid ${m.border}`,
  lineHeight: size === 'sm' ? '18px' : '20px', whiteSpace: 'nowrap',
});

export const RoleBadge: React.FC<{ role: RolePerm; size?: 'sm' | 'md' }> = ({ role, size = 'sm' }) => {
  const tier = roleTier(role);
  const m = ROLE_TIER_META[tier];
  const menus = role.perm_menus ?? 0;
  const detail = tier === 'none'
    ? `${m.label} — ${m.desc}`
    : `${m.label} · ${m.desc}\n메뉴 ${menus}개 / 조회 ${role.perm_read ?? 0} · 등록·수정 ${role.perm_write ?? 0} · 삭제 ${role.perm_delete ?? 0}`;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detail}</span>}>
      <span style={pill(m, size)}>
        <span style={{ fontSize: size === 'sm' ? 9 : 10, lineHeight: 1 }}>{m.icon}</span>
        {role.role_name}
      </span>
    </Tooltip>
  );
};

/** 등급만 보여주는 칩 (범례·셀렉트 옵션용) */
export const TierChip: React.FC<{ tier: RoleTier; text?: string; size?: 'sm' | 'md' }> = ({ tier, text, size = 'sm' }) => {
  const m = ROLE_TIER_META[tier];
  return (
    <span style={pill(m, size)}>
      <span style={{ fontSize: size === 'sm' ? 9 : 10, lineHeight: 1 }}>{m.icon}</span>
      {text ?? m.label}
    </span>
  );
};

/** 표 위 범례 — 색이 무엇을 뜻하는지 화면에서 바로 읽히게 한다 */
export const RoleTierLegend: React.FC<{ counts?: Partial<Record<RoleTier, number>> }> = ({ counts }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>권한 등급</span>
    {TIER_ORDER.map(t => (
      <Tooltip key={t} title={ROLE_TIER_META[t].desc}>
        <span>
          <TierChip tier={t} text={counts ? `${ROLE_TIER_META[t].label} ${counts[t] ?? 0}` : undefined} />
        </span>
      </Tooltip>
    ))}
  </span>
);
